import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatShortDateNoYear } from "@/lib/formatters";
import { enrichResult, getResultById } from "@/lib/api-client";
import { ApiError } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Linkedin,
  ExternalLink,
  Phone,
  Mail,
  User,
} from "lucide-react";
import type { CompanyResult, DeepEnrichmentData } from "@/types/api";

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 90_000;

type EnrichState =
  | "idle"
  | "loading"
  | "completed"
  | "failed"
  | "timeout"
  | "gone";

interface EnrichButtonProps {
  result: CompanyResult;
  jobId: string;
  /** Callback to update the result in parent state after enrichment completes */
  onEnriched?: (
    resultId: string,
    enrichmentData: Record<string, unknown>,
  ) => void;
  /** Compact mode for table rows (button only, no inline data) */
  compact?: boolean;
  /** Match action button sizing (h-11, full width) */
  fullWidth?: boolean;
}

function getDeepEnrichment(
  result: CompanyResult,
): DeepEnrichmentData | undefined {
  return (result.enrichment_data as Record<string, unknown>)
    ?.deep_enrichment as DeepEnrichmentData | undefined;
}

function resolveInitialState(result: CompanyResult): EnrichState {
  const deep = getDeepEnrichment(result);
  if (!deep) return "idle";
  switch (deep.status) {
    case "enriching":
      return "loading";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    default:
      return "idle";
  }
}

export function EnrichButton({
  result,
  jobId,
  onEnriched,
  compact = false,
  fullWidth = false,
}: EnrichButtonProps) {
  const { t } = useTranslation("results");
  const [state, setState] = useState<EnrichState>(() =>
    resolveInitialState(result),
  );
  const [deep, setDeep] = useState<DeepEnrichmentData | undefined>(() =>
    getDeepEnrichment(result),
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const mountedRef = useRef(true);

  // Sync state if parent passes a new result (e.g. after page-level refresh)
  useEffect(() => {
    const newDeep = getDeepEnrichment(result);
    if (newDeep) {
      setDeep(newDeep);
      setState(resolveInitialState(result));
    }
  }, [result]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    startTimeRef.current = Date.now();

    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) {
        stopPolling();
        return;
      }

      // Timeout check
      if (Date.now() - startTimeRef.current >= POLL_TIMEOUT_MS) {
        stopPolling();
        setState("timeout");
        return;
      }

      try {
        const updated = await getResultById(jobId, result.result_id);
        const updatedDeep = getDeepEnrichment(updated);
        if (!updatedDeep || updatedDeep.status === "enriching") return; // keep polling

        stopPolling();
        setDeep(updatedDeep);
        if (updatedDeep.status === "completed") {
          setState("completed");
          onEnriched?.(result.result_id, updated.enrichment_data);
        } else if (updatedDeep.status === "failed") {
          setState("failed");
        }
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) {
          stopPolling();
          setState("gone");
        }
        // Other poll errors: silently retry next interval
      }
    }, POLL_INTERVAL_MS);
  }, [jobId, result.result_id, onEnriched, stopPolling]);

  const handleEnrich = useCallback(async () => {
    stopPolling(); // Clear any stale polling interval before retrying
    setState("loading");
    setErrorMsg(null);

    try {
      const status = await enrichResult(jobId, result.result_id);
      // 202 or 409 both mean "enrichment is running" — start polling
      if (status === 202 || status === 409) {
        startPolling();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 404) {
          setState("gone");
          return;
        }
        if (err.statusCode === 429) {
          setErrorMsg(t("enrich.errorRateLimit"));
          setState("idle");
          return;
        }
      }
      // 5xx / network error
      setState("failed");
      setErrorMsg(t("enrich.errorGeneric"));
    }
  }, [jobId, result.result_id, startPolling]);

  // On page load, if status was already "enriching", resume polling
  useEffect(() => {
    if (state === "loading" && !pollRef.current) {
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ──

  // Gone state
  if (state === "gone") {
    return (
      <span className="text-xs text-muted-foreground italic">
        {t("enrich.gone")}
      </span>
    );
  }

  // Timeout state
  if (state === "timeout") {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
          <AlertTriangle className="h-3.5 w-3.5" />
          {t("enrich.timeout")}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-xs px-2"
          onClick={handleEnrich}
        >
          {t("enrich.retryButton")}
        </Button>
      </div>
    );
  }

  // Loading state
  if (state === "loading") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t("enrich.loading")}
      </span>
    );
  }

  // Failed state
  if (state === "failed") {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs text-destructive">
          <XCircle className="h-3.5 w-3.5" />
          {errorMsg || deep?.error || t("enrich.errorGeneric")}
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-xs px-2"
          onClick={handleEnrich}
        >
          {t("enrich.retryButton")}
        </Button>
      </div>
    );
  }

  // Completed state
  if (state === "completed" && deep) {
    if (compact) {
      return (
        <Badge variant="secondary" className="text-xs gap-1">
          <CheckCircle2 className="h-3 w-3 text-green-600" />
          {t("enrich.completed")}
        </Badge>
      );
    }

    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="font-medium text-green-700 text-xs">
            {t("enrich.completed")}
          </span>
          {deep.enriched_at && (
            <span className="text-xs text-muted-foreground ml-1">
              {formatShortDateNoYear(deep.enriched_at)}
            </span>
          )}
        </div>

        {/* Phone */}
        {deep.phone_formatted && (
          <div className="flex items-center gap-1.5 text-xs">
            <Phone className="h-3 w-3 text-muted-foreground" />
            <span>{deep.phone_formatted}</span>
            {deep.phone_verified && (
              <Badge variant="secondary" className="text-[10px] px-1 py-0">
                {t("enrich.verified")}
              </Badge>
            )}
          </div>
        )}

        {/* Email suggestions */}
        {deep.email_suggestions && deep.email_suggestions.length > 0 && (
          <div className="space-y-0.5">
            {deep.email_suggestions.map((email) => (
              <div key={email} className="flex items-center gap-1.5 text-xs">
                <Mail className="h-3 w-3 text-muted-foreground" />
                <a
                  href={`mailto:${email}`}
                  className="text-primary hover:underline"
                >
                  {email}
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Geschaeftsfuehrer */}
        {deep.geschaeftsfuehrer && deep.geschaeftsfuehrer.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {t("enrich.executives")}
            </p>
            {deep.geschaeftsfuehrer.map((gf, idx) => (
              <div
                key={`${gf.name}-${idx}`}
                className="flex items-center gap-1.5 text-xs"
              >
                <User className="h-3 w-3 text-muted-foreground" />
                <span>{gf.name}</span>
                {gf.title && (
                  <span className="text-muted-foreground">({gf.title})</span>
                )}
                <LinkedInLink
                  url={gf.linkedin_url}
                  searchUrl={gf.linkedin_search_url}
                />
              </div>
            ))}
          </div>
        )}

        {/* Company LinkedIn */}
        <div className="flex items-center gap-1.5 text-xs">
          <LinkedInLink
            url={deep.company_linkedin_url}
            searchUrl={deep.company_linkedin_search_url}
            label={t("enrich.linkedinIfAvailable")}
          />
        </div>
      </div>
    );
  }

  // Default (idle) state
  return (
    <div>
      <Button
        size={fullWidth ? "default" : "sm"}
        variant="outline"
        className={fullWidth ? "h-11 w-full gap-1" : "h-7 text-xs gap-1"}
        onClick={handleEnrich}
      >
        <Sparkles className="h-3.5 w-3.5" />
        {t("enrich.idleButton")}
      </Button>
      {errorMsg && <p className="text-xs text-amber-600 mt-1">{errorMsg}</p>}
    </div>
  );
}

// ── LinkedIn display logic ──

function LinkedInLink({
  url,
  searchUrl,
  label,
}: {
  url?: string;
  searchUrl?: string;
  label?: string;
}) {
  const { t } = useTranslation("results");
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        <Linkedin className="h-3 w-3" />
        {label || "LinkedIn"}
        <ExternalLink className="h-2.5 w-2.5" />
      </a>
    );
  }
  if (searchUrl) {
    return (
      <a
        href={searchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
      >
        <Linkedin className="h-3 w-3" />
        {t("enrich.linkedinSearch")}
        <ExternalLink className="h-2.5 w-2.5" />
      </a>
    );
  }
  return null;
}
